// VideoRoom.jsx (patient)
// Full-screen video call UI using the Chime SDK React Component Library.
// The MeetingProvider in App.jsx already holds the active session — this
// component just renders tiles and controls on top of it.

import { useState, useEffect } from "react";
import {
  VideoTileGrid,
  ControlBar,
  AudioInputControl,
  VideoInputControl,
  AudioOutputControl,
  ContentShareControl,
  useMeetingStatus,
  MeetingStatus,
  useLocalVideo,
} from "amazon-chime-sdk-component-library-react";
import Spinner from "./Spinner.jsx";

export default function VideoRoom({ patientName, doctorName, onLeave }) {
  const meetingStatus                   = useMeetingStatus();
  const { isVideoEnabled, toggleVideo } = useLocalVideo();
  const [leaving, setLeaving]           = useState(false);

  // Auto-enable camera when the VideoRoom mounts (call just started)
  useEffect(() => {
    if (meetingStatus === MeetingStatus.Succeeded && !isVideoEnabled) {
      toggleVideo();
    }
  }, [meetingStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLeave = async () => {
    setLeaving(true);
    await onLeave();
  };

  // Still connecting to media
  if (meetingStatus === MeetingStatus.Loading) {
    return (
      <div className="page-center">
        <Spinner message="Starting your video call…" />
      </div>
    );
  }

  // Connection error
  if (meetingStatus === MeetingStatus.Failed) {
    return (
      <div className="page-center">
        <div className="error-card">
          <span className="error-icon" aria-hidden="true">📡</span>
          <h2>Connection failed</h2>
          <p>Could not connect to the video call. Please refresh and try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="video-room">

      {/* Call header bar */}
      <div className="video-header" role="banner">
        <div className="video-logo">KSM <span>CareLink</span></div>
        <div className="video-participants" aria-label="Participants">
          <span className="participant-chip">
            <span aria-hidden="true">🧑</span> {patientName ?? "You"}
          </span>
          <span className="participant-divider" aria-hidden="true">↔</span>
          <span className="participant-chip">
            <span aria-hidden="true">👨‍⚕️</span> Dr. {doctorName ?? "Doctor"}
          </span>
        </div>
        <div className="video-status-dot" aria-label="Call in progress" title="Call in progress" />
      </div>

      {/* Video tile grid — handles local + all remote tiles automatically */}
      <div className="video-grid" role="main" aria-label="Video call">
        <VideoTileGrid
          className="tile-grid"
          noRemoteVideoView={
            <div className="no-remote-video" role="status">
              <div className="no-remote-avatar" aria-hidden="true">👨‍⚕️</div>
              <p>Dr. {doctorName ?? "Doctor"} has joined — waiting for video…</p>
            </div>
          }
        />
      </div>

      {/* Controls */}
      <div className="video-controls" role="toolbar" aria-label="Call controls">
        <ControlBar layout="undocked-horizontal" showLabels>
          <AudioInputControl />
          <VideoInputControl />
          <AudioOutputControl />
          <ContentShareControl />
        </ControlBar>

        <button
          className="btn btn-end-call"
          onClick={handleLeave}
          disabled={leaving}
          aria-label="End video call"
        >
          {leaving ? "Ending…" : "End Visit"}
        </button>
      </div>

    </div>
  );
}
